import { useQuery } from "@tanstack/react-query";

import { useTRPC } from "@/integrations/trpc/react";

export function isSetDone(
  teamAScore: number,
  teamBScore: number,
  winScore: number,
) {
  return (
    (teamAScore >= winScore && teamAScore - teamBScore >= 2) ||
    (teamBScore >= winScore && teamBScore - teamAScore >= 2)
  );
}

type MatchLiveness = {
  status: string;
  sets: { status: string }[];
};

/**
 * A set can linger as `in_progress` after the match is decided (e.g. a late
 * `startSet` tap on an already-completed match). Require the match itself to
 * be unfinished so it doesn't show as "Live" forever.
 */
export function hasInProgressSet(match: MatchLiveness): boolean {
  return (
    match.status !== "completed" &&
    match.sets.some((s) => s.status === "in_progress")
  );
}

/**
 * Match is live when play has started (any set in progress or completed) but
 * the match itself isn't done — covers the gap between sets.
 */
export function isMatchLive(match: MatchLiveness): boolean {
  return (
    match.status !== "completed" &&
    match.sets.some(
      (s) => s.status === "in_progress" || s.status === "completed",
    )
  );
}

type Scoreable = {
  teamAScore: number;
  teamBScore: number;
  winScore: number;
  status: string;
  endedAt: Date | null;
};

export function applyScores<T extends Scoreable>(
  current: T,
  teamAScore: number,
  teamBScore: number,
): T {
  const next: T = { ...current, teamAScore, teamBScore };
  const isDone = isSetDone(teamAScore, teamBScore, current.winScore);

  if (isDone) {
    return { ...next, status: "completed" as T["status"], endedAt: new Date() };
  }

  return next;
}

/**
 * Derive the authoritative score from score events. Events with snapshot
 * columns (from override/undo) reset the running tally; delta-only events
 * (from scorePoint) accumulate on top.
 */
export function scoreFromEvents(
  events: {
    team: string;
    delta: number;
    teamAScoreAfter?: number | null;
    teamBScoreAfter?: number | null;
  }[],
): { a: number; b: number } {
  let a = 0;
  let b = 0;
  for (const e of events) {
    if (e.teamAScoreAfter != null) {
      a = e.teamAScoreAfter;
      b = e.teamBScoreAfter ?? 0;
    } else {
      if (e.team === "A") a = Math.max(0, a + e.delta);
      else b = Math.max(0, b + e.delta);
    }
  }
  return { a, b };
}

/**
 * Return the effective score for a set. For in-progress sets with events,
 * compute live scores from the event stream. For completed/not-started sets,
 * use the materialized `teamAScore`/`teamBScore` columns.
 */
export function getSetScore(set: {
  teamAScore: number;
  teamBScore: number;
  status: string;
  scoreEvents?: {
    team: string;
    delta: number;
    teamAScoreAfter?: number | null;
    teamBScoreAfter?: number | null;
  }[];
}): { a: number; b: number } {
  if (set.status === "in_progress" && set.scoreEvents?.length) {
    return scoreFromEvents(set.scoreEvents);
  }
  return { a: set.teamAScore, b: set.teamBScore };
}

export type MatchIdProps = {
  poolMatchId?: number;
  playoffMatchId?: number;
};

export function useMatchTeams({ poolMatchId, playoffMatchId }: MatchIdProps) {
  const trpc = useTRPC();

  const { data: match } = useQuery({
    ...trpc.matches.get.queryOptions({ poolMatchId, playoffMatchId }),
    enabled: poolMatchId !== undefined || playoffMatchId !== undefined,
  });

  if (!match) {
    return undefined;
  }

  return {
    teamA: match.teamA,
    teamB: match.teamB,
  };
}

export function useMatchSets({ poolMatchId, playoffMatchId }: MatchIdProps) {
  const trpc = useTRPC();

  const { data: match } = useQuery({
    ...trpc.matches.get.queryOptions({ poolMatchId, playoffMatchId }),
    enabled: poolMatchId !== undefined || playoffMatchId !== undefined,
  });

  return match?.sets;
}
